import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Github, ExternalLink, Calendar, Users, CheckCircle, Clock } from 'lucide-react'
import { projects } from '@/data/projects'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { useScrollToTop } from '@/hooks/useScrollToTop'

// Helper function to convert YouTube URLs to embed format
function getEmbedUrl(url: string): string {
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0]
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url
  }
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0]
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url
  }
  // If already an embed URL or other video platform, return as is
  return url
}

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()
  const project = projects.find(p => p.id === projectId)
  
  // Scroll to top when component mounts
  useScrollToTop()

  if (!project) {
    return (
      <div className="min-h-screen bg-bg-light dark:bg-bg-dark flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark mb-4">
            Project Not Found
          </h1>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-light dark:bg-bg-dark">
      <div className="container mx-auto px-4 pt-16 pb-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link to="/">
            <Button variant="outline" className="group">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Projects
            </Button>
          </Link>
        </motion.div>

        {/* Project Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Project Image */}
            <div className="lg:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
                <div className="absolute top-4 right-4">
                  <Badge variant={project.status === 'completed' ? 'default' : 'secondary'}>
                    {project.status === 'completed' ? 'Completed' : 
                     project.status === 'in-progress' ? 'In Progress' :
                     project.status === 'prototype' ? 'Prototype' : 'Archived'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="lg:w-1/2 space-y-6">
              <div>
                <Link to="/" className="block group">
                  <h1 className="text-4xl font-bold text-text-light dark:text-text-dark mb-4 group-hover:text-accent transition-colors duration-200 cursor-pointer">
                    {project.title}
                  </h1>
                </Link>
                <p className="text-xl text-text-light/80 dark:text-text-dark/80 mb-6">
                  {project.description}
                </p>
              </div>

              {/* Project Meta */}
              <div className="flex flex-wrap gap-4 text-sm text-text-light/70 dark:text-text-dark/70">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {project.timeline}
                </div>
                {project.team && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {project.team}
                  </div>
                )}
              </div>

              {/* Technologies */}
              <div>
                <h3 className="text-lg font-semibold text-text-light dark:text-text-dark mb-3">
                  Technologies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <Badge key={tech} variant="outline">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-3">
                {project.links.github && (
                  <Button asChild>
                    <a href={project.links.github} target="_blank" rel="noopener noreferrer">
                      <Github className="w-4 h-4 mr-2" />
                      View Code
                    </a>
                  </Button>
                )}
                {project.links.live && (
                  <Button variant="outline" asChild>
                    <a href={project.links.live} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Live Demo
                    </a>
                  </Button>
                )}
                {project.links.demo && (
                  <Button variant="outline" asChild>
                    <a href={project.links.demo} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Watch Demo
                    </a>
                  </Button>
                )}
                {project.links.caseStudy && (
                  <Button variant="outline" asChild>
                    <a href={project.links.caseStudy} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Case Study
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Project Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">
                Problem
              </h3>
              <p className="text-text-light/80 dark:text-text-dark/80">
                {project.problem}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">
                Solution
              </h3>
              <p className="text-text-light/80 dark:text-text-dark/80">
                {project.solution}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">
                Result
              </h3>
              <p className="text-text-light/80 dark:text-text-dark/80">
                {project.result}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Demo Video Section */}
        {project.links.demo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-text-light dark:text-text-dark mb-6">
              Demo Video
            </h2>
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-black/5 dark:bg-black/20" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getEmbedUrl(project.links.demo)}
                className="absolute top-0 left-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${project.title} Demo Video`}
              />
            </div>
          </motion.div>
        )}

        {/* Assignments Section */}
        {project.assignments && project.assignments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-text-light dark:text-text-dark mb-8">
              Assignments ({project.assignments.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {project.assignments.map((assignment, index) => (
                <motion.div
                  key={assignment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                >
                  <Card className="h-full group hover:card-hover transition-all duration-300">
                    {/* Assignment Image */}
                    {assignment.image && (
                      <div className="relative h-32 overflow-hidden rounded-t-lg">
                        <img
                          src={assignment.image}
                          alt={assignment.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 right-2">
                          {assignment.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 bg-white rounded-full" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-500 bg-white rounded-full" />
                          )}
                        </div>
                      </div>
                    )}
                    
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg group-hover:text-accent transition-colors">
                        {assignment.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-text-light/70 dark:text-text-dark/70 text-sm">
                        {assignment.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-1">
                        {assignment.technologies.map((tech) => (
                          <Badge key={tech} variant="outline" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>

                      <Button 
                        asChild 
                        className="w-full group-hover:bg-accent group-hover:text-accent-contrast transition-colors"
                      >
                        <a 
                          href={assignment.githubUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center"
                        >
                          <Github className="w-4 h-4 mr-2" />
                          View Assignment
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Challenges & Learnings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="grid md:grid-cols-2 gap-8"
        >
          <div>
            <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">
              Challenges
            </h3>
            <ul className="space-y-2">
              {project.challenges.map((challenge, index) => (
                <li key={index} className="text-text-light/80 dark:text-text-dark/80 flex items-start">
                  <span className="text-accent mr-2">•</span>
                  {challenge}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-text-light dark:text-text-dark mb-4">
              Key Learnings
            </h3>
            <ul className="space-y-2">
              {project.learnings.map((learning, index) => (
                <li key={index} className="text-text-light/80 dark:text-text-dark/80 flex items-start">
                  <span className="text-accent mr-2">•</span>
                  {learning}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
